import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Flush-left, unboxed, with a red rule above each kicker. Type does the work.
export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up"
            >
                <div className="h-1 bg-accent w-full" />
                <span className="eyebrow text-accent block mt-4">{label}</span>
                <h2 className="font-display text-[2.1rem] sm:text-5xl lg:text-[4rem] font-black uppercase leading-[0.94] tracking-[-0.035em] mt-4 group-hover:text-accent transition-colors duration-100">
                    {article.title}
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mt-7">
                    <div className="lg:col-span-8 overflow-hidden bg-surface-raised aspect-[16/9]">
                        {article.thumbnail && (
                            <img
                                src={article.thumbnail}
                                alt={article.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                            />
                        )}
                    </div>
                    <div className="lg:col-span-4 flex flex-col">
                        {article.excerpt && (
                            <p className="text-[15px] leading-relaxed border-t border-foreground pt-4">{article.excerpt}</p>
                        )}
                        <div className="mt-auto pt-5 font-mono text-[11px] text-dim uppercase tracking-wider">
                            {article.author && <div className="truncate">{article.author}</div>}
                            <div className="mt-1">{timeAgo(article.published_at)}</div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 py-4 border-t border-line"
            >
                <div className="flex-1 min-w-0">
                    <span className="eyebrow text-accent">{label}</span>
                    <h3 className="font-display text-base font-bold leading-[1.2] tracking-[-0.015em] mt-2 group-hover:text-accent transition-colors duration-100">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] text-dim mt-2 block uppercase tracking-wider">
                        {timeAgo(article.published_at)}
                    </span>
                </div>
                {article.thumbnail && (
                    <div className="w-[92px] h-[68px] shrink-0 overflow-hidden bg-surface-raised">
                        <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group block animate-fade-up"
        >
            <div className="aspect-[3/2] overflow-hidden bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="h-0.5 bg-accent w-full mt-4" />
            <div className="pt-4">
                <span className="eyebrow text-accent">{label}</span>
                <h3 className="font-display text-xl font-bold leading-[1.15] tracking-[-0.02em] mt-2 group-hover:text-accent transition-colors duration-100">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-3 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                )}
                <div className="mt-4 font-mono text-[11px] text-dim uppercase tracking-wider flex items-center gap-3">
                    {article.author && <span className="truncate max-w-[150px]">{article.author}</span>}
                    {article.author && <span className="text-line">/</span>}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
